import { type FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AIAvatarIcon } from '@/features/ai-chat/ui/AIAvatarIcon';
import type { ChatInputHandle } from '@/features/ai-chat/ui/ChatInput';
import {
  SuggestedQuestionGrid,
  type SuggestedQuestionItem,
} from '@/features/ai-chat/ui/SuggestedQuestionGrid';
import { Text } from '@/shared/ui';
import { ChatInput } from './ChatInput';

type Props = {
  onSend: (message: string) => void;
};

/**
 * 事業内チャットの Welcome 画面。
 * 挨拶 + おすすめの質問 4 件 + 入力欄を縦に並べる（ITA-2375）
 *
 * おすすめの質問カードをクリックすると、即時送信せず入力欄にプロンプトをセット +
 * フォーカスを当てる（カスタマイズして使うことが多いため。ITA-2375 フィードバック対応）。
 */
export const ChatWelcomeView: FC<Props> = ({ onSend }) => {
  const { t } = useTranslation();
  const inputRef = useRef<ChatInputHandle>(null);

  const questions: SuggestedQuestionItem[] = [
    { id: 'q-summary-180', prompt: t('ai.chat.suggested-questions.q-summary-180') },
    {
      id: 'q-three-tier-pattern',
      prompt: t('ai.chat.suggested-questions.q-three-tier-pattern'),
    },
    { id: 'q-kpi', prompt: t('ai.chat.suggested-questions.q-kpi') },
    { id: 'q-budget-validity', prompt: t('ai.chat.suggested-questions.q-budget-validity') },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-1 flex-col items-center justify-center gap-6 overflow-auto">
        <div className="flex flex-col items-center gap-2">
          <AIAvatarIcon className="size-10" />
          <p className="bg-linear-to-r from-brand-500 to-brand-600 bg-clip-text font-medium text-transparent text-xl leading-7">
            {t('ai.chat.welcome-heading')}
          </p>
          <Text variant="description" className="text-center">
            {t('ai.chat.welcome-description')}
          </Text>
        </div>
        <SuggestedQuestionGrid
          title={t('ai.chat.suggested-questions.title')}
          questions={questions}
          onSelect={(prompt) => inputRef.current?.setMessageAndFocus(prompt)}
          columns={1}
        />
      </div>
      <div className="shrink-0 pb-4">
        <ChatInput
          ref={inputRef}
          onSend={onSend}
          placeholder={t('ai.chat.input-placeholder-welcome')}
        />
      </div>
    </div>
  );
};
